import type { RCard } from 'v2/types/api/billing'
import { errorToast } from 'v2/utils/toasts'
import { useDeleteCardMutation } from '../mutations'
import { useRazorpayDeleteCardMutation } from '../queries'
import { resolveCardDeleteRoute } from './card-delete-route'

/**
 * Deletes a saved card down whichever route its PSP requires.
 *
 * Both mutations are held here rather than at each call site so that "which
 * delete does this card need" is answered once. Previously every consumer had to
 * remember, and the card picker in the recharge modals did not — a Razorpay token
 * deleted from there went to the Stripe route, which reports success and leaves
 * the card chargeable.
 *
 * `isPending` is per-instance because each `CardRowActions` calls this hook, so a
 * row's spinner reflects that row's delete and not another's.
 */
export const useCardDeleteRouting = () => {
  const { mutate: deleteCard, isPending: isDeletingGeneric } = useDeleteCardMutation()
  const { mutate: deleteRazorpayCard, isPending: isDeletingRazorpay } =
    useRazorpayDeleteCardMutation()

  const deleteCardForPsp = (card: RCard) => {
    const route = resolveCardDeleteRoute(card)
    switch (route.kind) {
      case 'razorpay':
        deleteRazorpayCard(route.tokenId)
        return
      case 'generic':
        deleteCard({ card_mapping_id: route.cardMappingId })
        return
      default:
        // Refuse rather than fall back — the generic route would misroute the
        // token, so there is no safe second choice.
        errorToast({
          title: 'Failed',
          description: 'Could not identify this card. Please refresh and try again.',
        })
    }
  }

  return { deleteCardForPsp, isDeleting: isDeletingGeneric || isDeletingRazorpay }
}
